import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

export default function ModelViewer({ modelUrl = process.env.PUBLIC_URL + '/trumpet-cad.glb' }) {
  const containerRef = useRef(null);
  const stateRef = useRef({
    exploded: false,
    parts: [],       // { mesh, originalPos, explodeOffset, targetFactor, currentFactor }
    scaleFactor: 1,
  });
  const [exploded, setExploded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const labelRendererRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    const state = stateRef.current;
    state.exploded = false;
    state.parts = [];
    state.model = null;
    setExploded(false);
    setLoading(true);
    setProgress(0);
    let cancelled = false;
    let modelReady = false;
    let envReady = false;
    function tryFinishLoading() {
      if (cancelled || !modelReady || !envReady) return;
      // Wait one frame so Three.js renders the model with environment before hiding overlay
      requestAnimationFrame(() => {
        if (!cancelled) {
          setProgress(100);
          setLoading(false);
        }
      });
    }
    const ASSEMBLED_CAMERA = new THREE.Vector3(2.25, 0.95, 1.05);
    const EXPLODED_CAMERA = new THREE.Vector3(6.5, 2.5, 3.2);

    // Default explode layout (hand-tuned per part)
    const DEFAULT_LAYOUT = {
      'a':  { x: -65.433, y: 172.786, z: 325.678 },
      'b':  { x: 0, y: -80, z: 0 },
      'c':  { x: 27.083, y: -301.865, z: -313.516 },
      'd':  { x: 217.906, y: -1.326, z: 31.897 },
      'e':  { x: 293.794, y: -659.775, z: 185.192 },
      'f':  { x: -17.529, y: 85.412, z: 219.220 },
      'f1': { x: -531.941, y: 608.658, z: 210.571 },
      'f2': { x: -46.359, y: 185.352, z: 126.846 },
      'f3': { x: -159.498, y: 442.321, z: 243.296 },
      'f4': { x: 86.539, y: -204.877, z: -106.494 },
      'f5': { x: -156.141, y: -539.688, z: -108.101 },
      'g':  { x: 9.499, y: -50.352, z: -334.379 },
      'h':  { x: -37.568, y: 464.350, z: -267.011 },
      'i':  { x: -38.540, y: 230.723, z: -308.349 },
      'j':  { x: 120.586, y: -790.092, z: 10.714 },
      'j1': { x: -87.985, y: -472.174, z: 46.712 },
      'j2': { x: -173.641, y: -726.131, z: 65.737 },
      'l':  { x: 85.035, y: 28.070, z: 56.921 },
      'm':  { x: 17.679, y: -421.465, z: 298.946 },
      'n':  { x: 58.802, y: -30.557, z: 291.214 },
      'o':  { x: 93.737, y: -641.290, z: 281.331 },
      'p':  { x: -8.728, y: 74.723, z: 294.164 },
      'q':  { x: 5.747, y: 9.289, z: -157.721 },
    };

    const savedLayout = DEFAULT_LAYOUT;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.01, 100);
    camera.position.copy(ASSEMBLED_CAMERA);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    // CSS2D label renderer (overlays HTML labels on 3D scene)
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(width, height);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0';
    labelRenderer.domElement.style.left = '0';
    labelRenderer.domElement.style.pointerEvents = 'none';
    container.appendChild(labelRenderer.domElement);
    labelRendererRef.current = labelRenderer;

    // Part name mapping (mesh name → display label)
    const PART_NAMES = {
      'a': '喇叭口',
      'b': '主管体',
      'c': '第三活塞滑管',
      'd': '调音滑管',
      'e': '第一活塞滑管',
      'f': '第二活塞帽',
      'f1': '第一活塞帽',
      'f2': '第二活塞底盖',
      'f3': '第三活塞帽',
      'f4': '底座',
      'f5': '第三活塞底盖',
      'g': '第一活塞底盖',
      'h': '第一活塞弯管',
      'i': '第二活塞滑管',
      'j': '第二活塞弯管',
      'j1': '排水阀',
      'j2': '第三活塞滑管弯头',
      'l': '第一活塞管',
      'm': '第三活塞弯管',
      'n': '吹嘴接收管',
      'o': '支撑杆',
      'p': '喇叭弯管',
      'q': '吹嘴',
    };

    // Lighting
    const d1 = new THREE.DirectionalLight(0xffffff, 1);
    d1.position.set(5, 10, 7);
    d1.castShadow = true;
    d1.shadow.mapSize.width = 1024;
    d1.shadow.mapSize.height = 1024;
    scene.add(d1);

    // HDRI environment (same as reference site)
    const pmrem = new THREE.PMREMGenerator(renderer);
    new RGBELoader()
      .setPath('https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/')
      .load('royal_esplanade_1k.hdr', (hdr) => {
        if (cancelled) return;
        hdr.mapping = THREE.EquirectangularReflectionMapping;
        scene.environment = hdr;
        envReady = true;
        tryFinishLoading();
      }, undefined, () => {
        if (cancelled) return;
        // Fallback if HDRI fails
        const envScene = new THREE.Scene();
        envScene.background = new THREE.Color(0x555555);
        [[0xddaa33, 25, [5, 4, 4]], [0xffffff, 18, [-4, 6, -3]],
         [0xffd700, 20, [-3, -2, 5]]].forEach(([c, i, p]) => {
          const l = new THREE.PointLight(c, i, 50);
          l.position.set(...p);
          envScene.add(l);
        });
        scene.environment = pmrem.fromScene(envScene).texture;
        envReady = true;
        tryFinishLoading();
      });

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.8;
    controls.panSpeed = 0.8;
    controls.zoomSpeed = 1.0;
    controls.minDistance = 0.3;
    controls.maxDistance = 15;

    // Highlight material for clicked parts
    const highlightMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(1.0, 0.55, 0.08),
      metalness: 1.0,
      roughness: 0.15,
      envMapIntensity: 2.0,
    });
    state.highlightMat = highlightMat;

    // Raycaster for click
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let mouseDownPos = { x: 0, y: 0 };
    let mouseDownTime = 0;
    let lastTouchEnd = 0; // Prevent touch+mouse double-firing

    // Get client coordinates from mouse or touch event
    function getClientXY(e) {
      if (e.changedTouches && e.changedTouches.length > 0) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    }

    function onPointerDown(e) {
      // Mouse: only left button; Touch: always handle
      if (e.type === 'mousedown') {
        if (e.button !== 0) return;
        // Skip mouse event if a touch just ended (prevents double-firing)
        if (Date.now() - lastTouchEnd < 500) return;
      }
      const pos = getClientXY(e);
      mouseDownPos = { x: pos.x, y: pos.y };
      mouseDownTime = Date.now();
    }

    function onPointerUp(e) {
      const isTouch = e.type === 'touchend';
      if (e.type === 'mouseup') {
        if (e.button !== 0) return;
        if (Date.now() - lastTouchEnd < 500) return;
      }
      if (isTouch) lastTouchEnd = Date.now();

      const pos = getClientXY(e);
      const dx = pos.x - mouseDownPos.x;
      const dy = pos.y - mouseDownPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Touch: larger threshold (25px) because fingers are imprecise
      // Mouse: keep original 10px threshold
      const threshold = isTouch ? 25 : 10;
      if (dist < threshold && Date.now() - mouseDownTime < 500) {
        handleTap(pos, isTouch);
      }
    }

    // Cast a ray at NDC coordinates, return all intersections sorted by distance
    function raycastAll(ndcX, ndcY, meshes) {
      mouse.x = ndcX;
      mouse.y = ndcY;
      raycaster.setFromCamera(mouse, camera);
      return raycaster.intersectObjects(meshes, false);
    }

    // From multiple nearby hits, prefer the larger part (avoids small parts stealing clicks from big ones)
    function pickBestHit(hits) {
      if (hits.length === 0) return null;
      if (hits.length === 1) return hits[0];
      // Among hits within 20% extra distance of the closest, pick the largest part
      const closest = hits[0].distance;
      const threshold = closest * 1.2;
      let best = hits[0];
      let bestVolume = 0;
      for (const h of hits) {
        if (h.distance > threshold) break;
        const part = state.parts.find(p => p.mesh === h.object);
        if (part) {
          const box = new THREE.Box3().setFromObject(h.object);
          const size = box.getSize(new THREE.Vector3());
          const vol = size.x * size.y * size.z;
          if (vol > bestVolume) {
            bestVolume = vol;
            best = h;
          }
        }
      }
      return best;
    }

    function handleTap(pos, isTouch) {
      const rect = renderer.domElement.getBoundingClientRect();
      const ndcX = ((pos.x - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((pos.y - rect.top) / rect.height) * 2 + 1;

      const meshes = state.parts.map(p => p.mesh);

      // Try direct hit first — pick largest part among nearby hits
      const allHits = raycastAll(ndcX, ndcY, meshes);
      let hit = pickBestHit(allHits);

      // Touch: if missed, try a ring of nearby points (~20px radius in screen space)
      if (!hit && isTouch) {
        const offsetPx = 20;
        const ox = (offsetPx / rect.width) * 2;
        const oy = (offsetPx / rect.height) * 2;
        let candidates = [];
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const sx = ndcX + Math.cos(angle) * ox;
          const sy = ndcY + Math.sin(angle) * oy;
          candidates.push(...raycastAll(sx, sy, meshes));
        }
        candidates.sort((a, b) => a.distance - b.distance);
        hit = pickBestHit(candidates);
      }

      if (hit) {
        const clickedMesh = hit.object;
        const part = state.parts.find(p => p.mesh === clickedMesh);
        if (part) {
          const idx = state.parts.indexOf(part);
          console.log(`[Clicked] Part ${idx}, name="${clickedMesh.name}", offset=(${part.explodeOffset.x.toFixed(1)}, ${part.explodeOffset.y.toFixed(1)}, ${part.explodeOffset.z.toFixed(1)})`);
          const isOut = part.targetFactor > 0.5;
          part.targetFactor = isOut ? 0 : 1;
          part.mesh.material = isOut ? part.mesh.userData.originalMat : highlightMat;
          setExploded(state.parts.some(p => p.targetFactor > 0.5));
        }
      }
    }

    // Track touch count to ignore multi-finger gestures (pinch zoom)
    let activeTouchCount = 0;
    let wasMultiTouch = false;
    function onTouchStart(e) {
      activeTouchCount = e.touches.length;
      if (activeTouchCount > 1) {
        wasMultiTouch = true;
      }
      if (activeTouchCount === 1 && !wasMultiTouch) {
        onPointerDown(e);
      }
    }
    function onTouchEnd(e) {
      // Only handle as tap if it was a single-finger touch throughout
      if (activeTouchCount === 1 && !wasMultiTouch) {
        onPointerUp(e);
      }
      activeTouchCount = e.touches.length;
      // Reset multi-touch flag when all fingers are lifted
      if (activeTouchCount === 0) {
        wasMultiTouch = false;
      }
    }

    // Mouse events
    renderer.domElement.addEventListener('mousedown', onPointerDown);
    renderer.domElement.addEventListener('mouseup', onPointerUp);
    // Touch events
    renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: true });
    renderer.domElement.addEventListener('touchend', onTouchEnd, { passive: true });

    // Load model
    new GLTFLoader().load(modelUrl, (gltf) => {
      if (cancelled) return;
      state.parts = [];
      const model = gltf.scene;
      state.model = model;
      // Apply brass material + HDRI reflections
      const brassMat = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(0.83, 0.53, 0.03),
        metalness: 1.0,
        roughness: 0.08,
        clearcoat: 0.6,
        clearcoatRoughness: 0.02,
        envMapIntensity: 1,
        side: THREE.FrontSide,
      });
      state.brassMat = brassMat;
      model.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          child.material = brassMat;
          child.userData.originalMat = brassMat;
        }
      });

      // Compute in ORIGINAL space before scaling
      const origBox = new THREE.Box3().setFromObject(model);
      const origCenter = origBox.getCenter(new THREE.Vector3());
      const origSize = origBox.getSize(new THREE.Vector3());
      let partIndex = 0;

      model.traverse((child) => {
        if (child.isMesh) {
          const partBox = new THREE.Box3().setFromObject(child);
          const partCenter = partBox.getCenter(new THREE.Vector3());
          const partSize = partBox.getSize(new THREE.Vector3());
          let explodeOffset;
          if (savedLayout && savedLayout[child.name]) {
            const s = savedLayout[child.name];
            explodeOffset = new THREE.Vector3(s.x, s.y, s.z);
          } else {
            explodeOffset = new THREE.Vector3(0, 0, 0);
          }
          const originalQuaternion = child.quaternion.clone();
          console.log(`[Part ${partIndex}] name="${child.name}" size=(${partSize.x.toFixed(2)}, ${partSize.y.toFixed(2)}, ${partSize.z.toFixed(2)}) center=(${partCenter.x.toFixed(2)}, ${partCenter.y.toFixed(2)}, ${partCenter.z.toFixed(2)}) offset=(${explodeOffset.x.toFixed(1)}, ${explodeOffset.y.toFixed(1)}, ${explodeOffset.z.toFixed(1)})`);
          // Create CSS2D label positioned using geometry's own bounding box
          const labelDiv = document.createElement('div');
          labelDiv.textContent = PART_NAMES[child.name] || child.name;
          labelDiv.style.cssText = 'color:#fff;font-size:12px;padding:2px 8px;background:rgba(0,0,0,0.6);border-radius:4px;white-space:nowrap;opacity:0;transition:opacity 0.3s;pointer-events:auto;cursor:pointer;';
          // Click label to toggle assemble/explode
          const meshRef = child;
          labelDiv.addEventListener('click', () => {
            const part = state.parts.find(p => p.mesh === meshRef);
            if (part) {
              const isOut = part.targetFactor > 0.5;
              part.targetFactor = isOut ? 0 : 1;
              part.mesh.material = isOut ? part.mesh.userData.originalMat : state.highlightMat;
              setExploded(state.parts.some(p => p.targetFactor > 0.5));
            }
          });
          const label = new CSS2DObject(labelDiv);
          // Use geometry bounding box (mesh-local space) for accurate placement
          child.geometry.computeBoundingBox();
          const geoBB = child.geometry.boundingBox;
          const geoSize = new THREE.Vector3();
          geoBB.getSize(geoSize);
          const maxGeo = Math.max(geoSize.x, geoSize.y, geoSize.z);
          // Small parts: label below; large parts: label above
          const CAPS = ['f', 'f1', 'f2', 'f3', 'f5', 'g'];
          const isCap = CAPS.includes(child.name);
          const isSmall = !isCap && maxGeo < origSize.y * 0.15;
          const cx = (geoBB.min.x + geoBB.max.x) / 2;
          const cy = (geoBB.min.y + geoBB.max.y) / 2;
          const cz = (geoBB.min.z + geoBB.max.z) / 2;
          // Place label at geometry center
          label.position.set(cx, cy, cz);
          if (isCap) {
            // Caps: shift label up in screen space (center.y > 0.5 moves it above the anchor)
            label.center.set(0.5, 2.5);
          } else if (isSmall) {
            // Other small parts: label below
            label.center.set(0.5, -1.5);
          } else {
            // Large parts: label above
            label.center.set(0.5, 2.0);
          }
          child.add(label);

          state.parts.push({
            mesh: child,
            originalPos: child.position.clone(),
            explodeOffset,
            originalQuaternion,
            label: labelDiv,
            targetFactor: 0,   // 0 = assembled, 1 = exploded
            currentFactor: 0,
          });
          partIndex += 1;
        }
      });

      // Scale and center
      const maxDim = Math.max(origSize.x, origSize.y, origSize.z);
      state.scaleFactor = 2.5 / maxDim;
      model.scale.setScalar(state.scaleFactor);
      model.position.sub(origCenter.clone().multiplyScalar(state.scaleFactor));
      scene.add(model);

      controls.target.set(0, 0, 0);
      controls.update();

      modelReady = true;
      tryFinishLoading();
    }, (xhr) => {
      if (!cancelled && xhr.total > 0) {
        setProgress(Math.round((xhr.loaded / xhr.total) * 100));
      }
    });

    let animId;
    function animate() {
      animId = requestAnimationFrame(animate);

      // Animate each part individually
      let anyMoving = false;
      for (const part of state.parts) {
        if (Math.abs(part.currentFactor - part.targetFactor) > 0.001) {
          part.currentFactor += (part.targetFactor - part.currentFactor) * 0.08;
          if (Math.abs(part.currentFactor - part.targetFactor) < 0.002) {
            part.currentFactor = part.targetFactor;
          }
          const offset = part.explodeOffset.clone().multiplyScalar(part.currentFactor);
          part.mesh.position.copy(part.originalPos).add(offset);
          part.mesh.quaternion.copy(part.originalQuaternion);
          if (part.currentFactor === 0) {
            part.mesh.position.copy(part.originalPos);
            part.mesh.material = part.mesh.userData.originalMat;
          }
          anyMoving = true;
        }
        // Show label when exploded, hide when assembled
        if (part.label) {
          part.label.style.opacity = part.currentFactor > 0.5 ? '1' : '0';
        }
      }

      // Camera auto zoom
      if (anyMoving) {
        const anyExploded = state.parts.some(p => p.targetFactor > 0.5);
        const camTarget = anyExploded ? EXPLODED_CAMERA : ASSEMBLED_CAMERA;
        camera.position.lerp(camTarget, 0.035);
      }

      controls.update();
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
    }
    animate();

    function onResize() {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      labelRenderer.setSize(w, h);
    }
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('mousedown', onPointerDown);
      renderer.domElement.removeEventListener('mouseup', onPointerUp);
      renderer.domElement.removeEventListener('touchstart', onTouchStart);
      renderer.domElement.removeEventListener('touchend', onTouchEnd);
      cancelAnimationFrame(animId);
      controls.dispose();
      renderer.dispose();
      pmrem.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      if (container.contains(labelRenderer.domElement)) {
        container.removeChild(labelRenderer.domElement);
      }
    };
  }, [modelUrl]);

  // Explode all / Assemble all
  function handleToggle() {
    const state = stateRef.current;
    const hasSplitParts = state.parts.some(part => part.targetFactor > 0.5 || part.currentFactor > 0.05);
    const next = !hasSplitParts;
    setExploded(next);
    stateRef.current.exploded = next;
    for (const part of state.parts) {
      part.targetFactor = next ? 1 : 0;
      part.mesh.material = next ? state.highlightMat : part.mesh.userData.originalMat;
    }
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        background: 'linear-gradient(180deg, #202124 0%, #3b3b3d 48%, #161719 100%)',
      }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

      {/* Loading overlay */}
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(32, 33, 36, 0.92)',
            zIndex: 10,
          }}
        >
          <div style={{ color: '#ccc', fontSize: 16, marginBottom: 20 }}>
            模型加载中...
          </div>
          <div
            style={{
              width: 260,
              height: 6,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.15)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(90deg, #d4a017, #f0c040)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <div style={{ color: '#888', fontSize: 13, marginTop: 10 }}>
            {progress}%
          </div>
        </div>
      )}

      {/* Explode all / Assemble all button */}
      <button
        onClick={handleToggle}
        style={{
          position: 'absolute',
          top: 24,
          right: 24,
          background: exploded ? 'rgba(200, 80, 40, 0.85)' : 'rgba(80, 80, 80, 0.75)',
          color: '#fff',
          border: 'none',
          padding: '14px 28px',
          borderRadius: 24,
          fontSize: 16,
          cursor: 'pointer',
          touchAction: 'manipulation',
          backdropFilter: 'blur(4px)',
          transition: 'background 0.3s',
        }}
      >
        {exploded ? '🔧 全部组装' : '💥 全部拆分'}
      </button>

      {/* Hint bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(80, 80, 80, 0.75)',
          color: '#fff',
          padding: '8px 24px',
          borderRadius: 20,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          backdropFilter: 'blur(4px)',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 16 }}>💡</span>
        <span>拖拽旋转 | 双指缩放 | 点击/触摸零件拆分/复原</span>
      </div>
    </div>
  );
}
